import InteractiveContainer from './interactive-container';
import SelectEvent from './select-event';

export default class SelectableContainer<Item> extends InteractiveContainer {
	/**
	 * The payload value will be sent along with the SelectEvent as the payload property.
	 *
	 * @param {null | Item} value The payload value
	 */
	public set payload(value: Item | null) {
		this.#payload = value;
	}

	public get payload(): Item | null {
		return this.#payload;
	}

	/**
	 * The selected property is set to true when the SelectableContainer is clicked or Space / Enter is released while focused.
	 *
	 * When the selected property is set from within the SelectableContainer, a SelectEvent is dispatched that bubbles up through the DOM tree.
	 *
	 * @param {boolean} value True if selected, false otherwise
	 */
	public set selected(value: boolean) {
		if (this.#selected === value) {
			return;
		}
		assertIsBoolean(value);
		this.#selected = value;
		this.#selectedChanged = true;
		this.invalidateProperties();
	}

	public get selected(): boolean {
		return this.#selected;
	}

	#payload: Item | null;
	#selected: boolean;
	#selectedChanged: boolean;

	public constructor() {
		super();
		this.#selected = false;
		this.#selectedChanged = false;
		this.#payload = null;
	}

	protected override clicked(event: Event): void {
		super.clicked(event);
		this.selected = !this.selected;
		this.#dispatchSelectEvent();
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#selectedChanged) {
			this.#commitSelected();
		}
	}

	/**
	 * The selectedChanged method will be invoked every time the selected property changes.
	 *
	 * Override the method in sub classes to specify how the Component will behave or be styled.
	 */
	protected selectedChanged(): void {
		// override in sub classes
	}

	#commitSelected(): void {
		this.#selectedChanged = false;
		this.selectedChanged();
	}

	#dispatchSelectEvent(): void {
		this.dispatchEvent(new SelectEvent<Item>(this.selected, this.payload));
	}
}
customElements.define('ac-selectable-container', SelectableContainer);

function assertIsBoolean(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be of type boolean`);
}
